import { useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle, toast } from 'component-library';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePatchPlotPortfolioProduct, usePostPlotPortfolioProduct } from '@/api/rest/resources/plotPortfolioProducts';
import { PlotPortfolioProduct } from '@/api/rest/resources/types/plotPortfolioProducts';
import { Form, NumericFormField, OptionalNumericFormField, TextFormField } from '@/components';
import { PLOT_PORTFOLIO_PRODUCTS_QUERY_KEY } from '@/hooks/usePlotPortfolioProducts';

type PlotPortfolioProductFormValues = {
  name: string;
  display_name: string;
  initial_setup_cost_credits: string;
  plot_creation_cost_credits: string;
  max_plots: string;
  max_hectares: string;
};

type PlotPortfolioProductFormDialogProps = {
  open: boolean;
  onClose: () => void;
  editItem?: PlotPortfolioProduct;
};

export const PlotPortfolioProductFormDialog = ({ open, onClose, editItem }: PlotPortfolioProductFormDialogProps) => {
  const { t } = useTranslation();
  const isEditing = !!editItem;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className='min-w-[30vw]'>
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t('shared.ndiProducts.plotPortfolioDialog.title.update')
              : t('shared.ndiProducts.plotPortfolioDialog.title.create')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>{open && <PlotPortfolioProductFormContent editItem={editItem} onClose={onClose} />}</DialogBody>
      </DialogContent>
    </Dialog>
  );
};

const PlotPortfolioProductFormContent = ({
  editItem,
  onClose,
}: {
  editItem?: PlotPortfolioProduct;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const postMutation = usePostPlotPortfolioProduct();
  const patchMutation = usePatchPlotPortfolioProduct();
  const invalidNumberMessage = t('shared.ndiProducts.formRules.invalidNumber');
  const noLimitPlaceholder = t('shared.ndiProducts.noLimit');

  const form = useForm<PlotPortfolioProductFormValues>({
    defaultValues: {
      name: editItem?.name ?? '',
      display_name: editItem?.display_name ?? '',
      initial_setup_cost_credits: String(editItem?.initial_setup_cost_credits ?? 0),
      plot_creation_cost_credits: String(editItem?.plot_creation_cost_credits ?? 0),
      max_plots: editItem?.max_plots !== null && editItem?.max_plots !== undefined ? String(editItem.max_plots) : '',
      max_hectares:
        editItem?.max_hectares !== null && editItem?.max_hectares !== undefined ? String(editItem.max_hectares) : '',
    },
  });

  const isSubmitting = postMutation.isPending || patchMutation.isPending;

  const handleSubmit = form.handleSubmit(async (values) => {
    const bodyData = {
      name: values.name,
      display_name: values.display_name,
      initial_setup_cost_credits: Number(values.initial_setup_cost_credits),
      plot_creation_cost_credits: Number(values.plot_creation_cost_credits),
      max_plots: values.max_plots === '' ? null : Number(values.max_plots),
      max_hectares: values.max_hectares === '' ? null : Number(values.max_hectares),
    };

    try {
      if (editItem) {
        await patchMutation.mutateAsync({ pathVariables: { id: editItem.id }, bodyData });
      } else {
        await postMutation.mutateAsync({ bodyData });
      }

      queryClient.invalidateQueries({ queryKey: [PLOT_PORTFOLIO_PRODUCTS_QUERY_KEY] });
      toast({
        type: 'success',
        title: editItem ? t('shared.ndiProducts.toast.updateSuccess') : t('shared.ndiProducts.toast.createSuccess'),
      });
      onClose();
    } catch {
      toast({ type: 'error', title: t('shared.ndiProducts.toast.saveError') });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
        <TextFormField control={form.control} name='name' label={t('shared.ndiProducts.fields.name')} />
        <TextFormField control={form.control} name='display_name' label={t('shared.ndiProducts.fields.displayName')} />
        <NumericFormField
          control={form.control}
          name='initial_setup_cost_credits'
          label={t('shared.ndiProducts.fields.initialSetupCostCredits')}
          invalidNumberMessage={invalidNumberMessage}
        />
        <NumericFormField
          control={form.control}
          name='plot_creation_cost_credits'
          label={t('shared.ndiProducts.fields.plotCreationCostCredits')}
          invalidNumberMessage={invalidNumberMessage}
        />
        <OptionalNumericFormField
          control={form.control}
          name='max_plots'
          label={t('shared.ndiProducts.fields.maxPlots')}
          invalidNumberMessage={invalidNumberMessage}
          placeholder={noLimitPlaceholder}
        />
        <OptionalNumericFormField
          control={form.control}
          name='max_hectares'
          label={t('shared.ndiProducts.fields.maxHectares')}
          invalidNumberMessage={invalidNumberMessage}
          placeholder={noLimitPlaceholder}
        />
        <Button type='submit' className='self-end w-fit px-6' loading={isSubmitting} disabled={isSubmitting}>
          {t('global.ui.buttons.save')}
        </Button>
      </form>
    </Form>
  );
};
